// Motion layer (all decorative, all skipped for prefers-reduced-motion):
// - scroll reveals for cards and section heads, badges "slap" on as their card arrives (CSS)
// - headlines rise letter by letter (split into masked characters; screen readers get the plain text)
// - the manifesto "reads itself": words fill in as you scroll (CSS scroll-driven animation)
// - the mascot sketches draw themselves; records peek out of sleeves on touch screens
// Content already on screen at load is never hidden, except the home hero title on the first visit of a
// session (the intro), which a CSS fail-safe reveals if this script never runs.
(() => {
  const root = document.documentElement;
  const reduce = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
  if (reduce || !('IntersectionObserver' in window)) {
    root.classList.remove('pr-intro');
    return;
  }

  const observer = new IntersectionObserver(
    (entries) => {
      entries.forEach((entry) => {
        if (!entry.isIntersecting) return;
        entry.target.classList.add('is-inview');
        observer.unobserve(entry.target);
      });
    },
    { threshold: 0.1 }
  );

  const fold = window.innerHeight;
  const belowFold = (el) => el.getBoundingClientRect().top >= fold;

  // ---- Split headlines into masked characters ----
  // The letters are aria-hidden and the heading carries its text as aria-label, so screen readers hear
  // the title once and the page text holds it once (a visually hidden copy used to double it:
  // "ReleasesReleases"). Headlines are plain text (no links inside), which is what makes the label safe.
  const split = (el) => {
    if (el.classList.contains('pr-split')) return;
    if (el.querySelector('a, button, input, select, textarea')) return;
    const label = [];
    const labelWalker = document.createTreeWalker(el, NodeFilter.SHOW_TEXT, {
      acceptNode: (node) =>
        node.parentElement.closest('svg, [aria-hidden="true"]') ? NodeFilter.FILTER_REJECT : NodeFilter.FILTER_ACCEPT,
    });
    while (labelWalker.nextNode()) label.push(labelWalker.currentNode.textContent);
    const walker = document.createTreeWalker(el, NodeFilter.SHOW_TEXT, {
      acceptNode: (node) =>
        node.textContent.trim() && !node.parentElement.closest('svg, .visually-hidden')
          ? NodeFilter.FILTER_ACCEPT
          : NodeFilter.FILTER_REJECT,
    });
    const nodes = [];
    while (walker.nextNode()) nodes.push(walker.currentNode);
    let i = 0;
    nodes.forEach((node) => {
      const visual = document.createElement('span');
      visual.setAttribute('aria-hidden', 'true');
      node.textContent.split(/(\s+)/).forEach((part) => {
        if (!part) return;
        if (/^\s+$/.test(part)) return visual.append(document.createTextNode(' '));
        const word = document.createElement('span');
        word.className = 'pr-split__word';
        [...part].forEach((ch) => {
          const c = document.createElement('span');
          c.className = 'pr-split__char';
          c.style.setProperty('--i', i++);
          c.textContent = ch;
          word.append(c);
        });
        visual.append(word);
      });
      node.replaceWith(visual);
    });
    el.setAttribute('aria-label', label.join('').replace(/\s+/g, ' ').trim());
    el.classList.add('pr-split');
  };

  const HEADLINES = [
    '.pr-section-title',
    '.pr-collection__title',
    '.pr-release__title',
    '.pr-article__title',
    '.pr-manifesto__heading',
    '.pr-direct-line__title',
    '.pr-chapter__title',
    '.pr-about-hero__title',
  ].join(',');

  document.querySelectorAll(HEADLINES).forEach((el) => {
    if (!belowFold(el)) return;
    split(el);
    observer.observe(el);
  });

  // Hero titles: split them all; the active one animates now only on the session's first visit (intro),
  // the others animate each time their slide comes in (pr-hero.js toggles .is-entering).
  const heroTitles = document.querySelectorAll('.pr-hero__title');
  heroTitles.forEach(split);
  heroTitles.forEach((el) => {
    if (!el.closest('.is-active') || !root.classList.contains('pr-intro')) el.classList.add('is-inview');
  });
  if (root.classList.contains('pr-intro')) {
    try {
      sessionStorage.setItem('pr-intro', '1');
    } catch (e) {
      /* storage blocked */
    }
    const active = document.querySelector('.pr-hero__slide.is-active .pr-hero__title');
    requestAnimationFrame(() => {
      root.classList.remove('pr-intro');
      requestAnimationFrame(() => active?.classList.add('is-inview'));
    });
  }

  // ---- Manifesto reads itself (only where CSS scroll-driven animations exist) ----
  if (window.CSS?.supports?.('animation-timeline: view()')) {
    document.querySelectorAll('.pr-manifesto__body').forEach((body) => {
      const walker = document.createTreeWalker(body, NodeFilter.SHOW_TEXT);
      const nodes = [];
      while (walker.nextNode()) if (walker.currentNode.textContent.trim()) nodes.push(walker.currentNode);
      nodes.forEach((node) => {
        const fragment = document.createDocumentFragment();
        node.textContent.split(/(\s+)/).forEach((part) => {
          if (!part) return;
          if (/^\s+$/.test(part)) return fragment.append(document.createTextNode(part));
          const word = document.createElement('span');
          word.className = 'pr-read';
          word.textContent = part;
          fragment.append(word);
        });
        node.replaceWith(fragment);
      });
    });
  }

  // ---- Scroll reveals ----
  const REVEAL = [
    '.pr-section-head',
    '.pr-grid > li',
    '.pr-merch__row > li',
    '.pr-journal__list > li',
    '.pr-manifesto__single',
    '.pr-manifesto__sign',
    '.pr-fact',
    '.pr-item',
    '.pr-person',
    '.pr-about-cta__grid > li',
    '.pr-about-name__step',
  ].join(',');

  document.querySelectorAll(REVEAL).forEach((el) => {
    if (!belowFold(el)) return;
    const index = Array.prototype.indexOf.call(el.parentElement.children, el);
    el.style.setProperty('--pr-delay', `${(index % 4) * 80}ms`);
    el.classList.add('pr-reveal');
    observer.observe(el);
  });

  // ---- Mascot sketches draw themselves ----
  document.querySelectorAll('.pr-smiley--sketch').forEach((el) => {
    el.classList.add('pr-draw');
    observer.observe(el);
  });

  // ---- Touch screens: records peek out of their sleeves once ----
  if (window.matchMedia('(hover: none)').matches) {
    document.querySelectorAll('.pr-card__media-wrap').forEach((el) => observer.observe(el));
  }
})();
